"use client"

import type { AnnouncementLevel } from "@repo/types"
import { AlertOctagon, AlertTriangle, Info, X } from "lucide-react"
import { useEffect, useState } from "react"
import { useMyAnnouncements } from "../hooks/api/platform/use-platform"
import { cn } from "../lib/utils"
import { safeSetItem } from "../lib/utils/localStorage"
import { dismissKey, isDismissed } from "../features/platform/platform.helpers"

const LEVEL_STYLES: Record<AnnouncementLevel, { className: string; Icon: typeof Info }> = {
  info: { className: "border-primary/20 bg-primary/10 text-foreground", Icon: Info },
  warning: { className: "border-warning/40 bg-warning-muted text-foreground", Icon: AlertTriangle },
  critical: { className: "border-destructive/30 bg-destructive text-destructive-foreground", Icon: AlertOctagon },
}

/** Annonces de la plateforme (espace /superadmin), en haut de toutes les pages du dashboard. */
export function AnnouncementBanner() {
  const { data: announcements } = useMyAnnouncements()
  const [, setVersion] = useState(0)
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  if (!mounted || !announcements?.length) return null
  const visible = announcements.filter((announcement) => !isDismissed(announcement))
  if (visible.length === 0) return null

  return (
    <div className="flex flex-col">
      {visible.map((announcement) => {
        const { className, Icon } = LEVEL_STYLES[announcement.level]
        return (
          <div key={announcement.id} role="status" className={cn("flex items-start gap-3 border-b px-4 py-2 text-sm", className)}>
            <Icon className="mt-0.5 h-4 w-4 shrink-0" />
            <p className="flex-1 min-w-0 whitespace-pre-line">{announcement.message}</p>
            {announcement.dismissible && (
              <button
                type="button"
                aria-label="Masquer l'annonce"
                className="rounded p-0.5 opacity-70 hover:opacity-100"
                onClick={() => {
                  safeSetItem(dismissKey(announcement), announcement.updated_at)
                  setVersion((v) => v + 1)
                }}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
